import { Period, formatDateWithDayName, formatPeriod } from '@WombatTechnology/nitte-time-lib'
import { format } from 'date-fns'
import en from 'date-fns/locale/en-US'
import ja from 'date-fns/locale/ja'
import { endOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { timeZoneLabel, browserTimeZone } from '@/common/helpers/browserTimeZone'
import { getTimeZoneAbbreviation } from '@/common/helpers/timezoneUtils'

export type PeriodTextLang = 'ja' | 'en'

export interface Formatter {
  formatPeriod(period: Period): string
}

export const getFormatter = (lang: PeriodTextLang, secondaryTZ?: string | null): Formatter => {
  switch (lang) {
    case 'ja':
      return createJaFormatter(secondaryTZ)
    case 'en':
      return createEnFormatter(secondaryTZ)
  }
}

const isAllDay = (period: Period): boolean => {
  return period.end.getTime() === endOfDay(period.end).getTime()
}

// 日本語
const createJaFormatter = (secondaryTZ?: string | null): Formatter => ({
  formatPeriod(period: Period): string {
    const base = isAllDay(period) ? formatDateWithDayName(period.start) : formatPeriod(period)
    if (!secondaryTZ || isAllDay(period)) return `・ ${base}`

    const localTZLabel = timeZoneLabel()
    const secDate = formatInTimeZone(period.start, secondaryTZ, 'M月d日(eee)', { locale: ja })
    const secStart = formatInTimeZone(period.start, secondaryTZ, 'HH:mm')
    const secEnd = formatInTimeZone(period.end, secondaryTZ, 'HH:mm')
    const secTZLabel = getTimeZoneAbbreviation(secondaryTZ)

    // ベース部分にもローカルTZラベルを付与
    const basePart = `${formatPeriod(period)} ${localTZLabel}`
    return `・${secDate} ${secStart}~${secEnd} ${secTZLabel} (${basePart})`
  }
})

// 英語
const createEnFormatter = (secondaryTZ?: string | null): Formatter => ({
  formatPeriod(period: Period): string {
    const datePart = format(period.start, 'EEEE, MMMM d', { locale: en })
    if (isAllDay(period)) return datePart

    const startTime = format(period.start, 'h:mm a', { locale: en })
    const endTime = format(period.end, 'h:mm a', { locale: en })
    const tz = timeZoneLabel()
    const base = `${datePart} at ${startTime} - ${endTime} ${tz}`

    if (!secondaryTZ) return `- ${base}`

    const secDate = formatInTimeZone(period.start, secondaryTZ, 'EEEE, MMMM d', { locale: en })
    const secStart = formatInTimeZone(period.start, secondaryTZ, 'h:mm a', { locale: en })
    const secEnd = formatInTimeZone(period.end, secondaryTZ, 'h:mm a', { locale: en })
    const secTZLabel = getTimeZoneAbbreviation(secondaryTZ)
    return `- ${secDate} at ${secStart} - ${secEnd} ${secTZLabel} (${base})`
  }
})
